import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { AppSettingsService } from './app-settings.service';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { MapStyle } from '@nx-fe/map';

/**
 * Dashboard config interface definition.
 * The dashboard config provides the ability to maintain the current user's settings on dashboard page.
 */
export interface IDashboardConfig {
  /** Boolean that indicates whether the infographics panel should be hidden */
  IsCollapsed: boolean;
}

export interface IOnboardingConfig {
  HasOnboarded?: boolean;
  HasOnboardedIncidents?: boolean;
  HasOnboardedDeviceAnalysis?: boolean;
  HasOnboardedSabha?: boolean;
  IsWelcomeRequired?: boolean;
}

interface UserPreferences {
  mapStyle: MapStyle;
}

/**
 * User Settings Service.
 * The user settings service provides the ability to maintain the current user's settings.
 */

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  onboardingReplaySub: ReplaySubject<IOnboardingConfig>;

  private readonly _userPreferencesStorageKey: string = 'UserPreferences';

  constructor(
    private http: HttpClient,
    @Inject('LOCALSTORAGE')
    private localStorage: Storage,
    private appSettingsService: AppSettingsService,
    private authenticationService: AuthenticationService
  ) {}

  /** Retrieve Dashboard Configurations */
  public getDashboardConfig(): Observable<IDashboardConfig> {
    return this.http.get<IDashboardConfig>(this.appSettingsService.accountServiceUrl + 'config/dashboard');
  }

  /** Update Dashboard Configurations  */
  public updateDashboardConfig = (param: IDashboardConfig): Observable<IDashboardConfig> => {
    return this.http.put<IDashboardConfig>(this.appSettingsService.accountServiceUrl + 'config/dashboard', param);
  };

  /** Retrieve Onboarding Configurations */
  public getOnboardingConfig(): Observable<IOnboardingConfig> {
    if (!this.onboardingReplaySub) {
      this.onboardingReplaySub = new ReplaySubject<IOnboardingConfig>(1);
      return this.http.get<IOnboardingConfig>(this.appSettingsService.accountServiceUrl + 'config/onboarding').pipe(
        tap(config => {
          this.onboardingReplaySub.next(config);
        }),
        switchMap(() => this.onboardingReplaySub.asObservable())
      );
    }
    return this.onboardingReplaySub.pipe(take(1));
  }

  /** Update Onboarding Configurations  */
  public updateOnboardingConfig = (param: IOnboardingConfig): Observable<IOnboardingConfig> => {
    return this.http
      .put<IOnboardingConfig>(this.appSettingsService.accountServiceUrl + 'config/onboarding', param)
      .pipe(
        tap(() => {
          this.onboardingReplaySub.next(param);
        }),
        map(() => param)
      );
  };

  public get onboardingAlreadyShown(): boolean {
    return this.localStorage['onboardingAlreadyShown'] || false;
  }

  public set onboardingAlreadyShown(value: boolean) {
    this.localStorage['onboardingAlreadyShown'] = value;
  }

  public setUserPreferences = <T extends UserPreferences, K extends keyof T>(key: K, value: T[K]): Observable<void> => {
    const localStore = { ...JSON.parse(this._getLocalStoreData()) };
    const currentUserId: string = this.authenticationService.getUserId();
    if (!localStore[currentUserId]) {
      localStore[currentUserId] = {};
    }

    localStore[currentUserId][key] = value;

    this._setLocalStoreData(JSON.stringify(localStore));

    return of();
  };

  public getUserPreferences = <T extends UserPreferences, K extends keyof T>(key: K): Observable<T[K]> => {
    const localStore = { ...JSON.parse(this._getLocalStoreData()) };
    const currentUserId: string = this.authenticationService.getUserId();

    const userPreferences = localStore[currentUserId] ?? {};
    return of(userPreferences[key]);
  };

  private _getLocalStoreData(): string {
    return this.localStorage.getItem(this._userPreferencesStorageKey) || '{}';
  }

  private _setLocalStoreData(data: string) {
    this.localStorage.setItem(this._userPreferencesStorageKey, data);
  }
}
